import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import { INITIAL_MARKET_STATE, marketReducer, type KeyedMarketState, type QuantMarketState } from '@/app/hooks/useQuantMarket'
import { paramsChangeKey } from '@/app/lib/quant/previewParams'
import { quantApi } from '@/app/lib/quant/quantApi'
import { viewFailureEffect } from '@/app/lib/quant/quantErrors'
import type { ParamValue } from '@/app/lib/quant/types'
import type { StrategyViewSession, ViewFailure, ViewRate } from '@/app/lib/quant/viewProtocol'

/** A params edit reopens the view after this quiet period (a strategy view has no params-only update). */
export const STRATEGY_VIEW_DEBOUNCE_MS = 400

export interface UseQuantStrategyViewArgs {
  slug: string
  instrument: string
  tf: string
  bars: number
  /** Coerced param values, or `null` while the form is invalid (nothing is opened or sent). */
  params: Record<string, ParamValue> | null
  rate: ViewRate
  /** False = no view is opened (the instrument has no live feed, or the feed is not known yet). */
  enabled: boolean
}

export interface UseQuantStrategyView {
  state: QuantMarketState
  /** The refusal code that sends the page back to the still REST preview, or `null` while the live view stands. */
  fallbackCode: ViewFailure['code'] | null
  /** One user-initiated reopen after a drop. */
  reconnect: () => void
}

/**
 * One live `strategy` view (spec 070 D12). The overlay is the `indicator` frame with id `strategy`; the
 * frames go through the same `marketReducer` as Manual. A refusal that the page answers with the REST
 * preview (`view_unavailable`, `instrument_not_live`, `view_cap_reached`, bridge down) is final for the
 * slug, instrument, timeframe and bars shown: a later params edit does not retry it. A rate change sends
 * `rate` and never reopens.
 */
export function useQuantStrategyView({ slug, instrument, tf, bars, params, rate, enabled }: UseQuantStrategyViewArgs): UseQuantStrategyView {
  const baseKey = `${slug}|${instrument}|${tf}|${bars}`
  const [attempt, setAttempt] = useState(0)
  const [fell, setFell] = useState<{ key: string; code: ViewFailure['code'] } | null>(null)
  const fellBack = fell !== null && fell.key === baseKey

  // The params key the view is open for: follows the form after a quiet period (the first valid set is immediate).
  const liveParamsKey = params === null ? null : paramsChangeKey(params)
  const [openFor, setOpenFor] = useState<string | null>(liveParamsKey)
  // The first valid params open at once (derived during render, like `useQuantPreview`'s arming).
  if (openFor === null && liveParamsKey !== null) setOpenFor(liveParamsKey)
  const paramsRef = useRef(params)
  useEffect(() => {
    paramsRef.current = params
  })
  useEffect(() => {
    if (liveParamsKey === null || liveParamsKey === openFor) return
    const id = setTimeout(() => setOpenFor(liveParamsKey), STRATEGY_VIEW_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [liveParamsKey, openFor])

  const active = enabled && !fellBack && openFor !== null
  const key = `${baseKey}|${openFor ?? ''}|${attempt}`
  const [state, dispatch] = useReducer(marketReducer, { ...INITIAL_MARKET_STATE, key })
  const rateRef = useRef(rate)
  const sessionRef = useRef<StrategyViewSession | null>(null)
  const sentRateRef = useRef(rate)
  useEffect(() => {
    rateRef.current = rate
  })

  useEffect(() => {
    const values = paramsRef.current
    if (!active || values === null) return
    const startRate = rateRef.current
    const session = quantApi.openStrategyView({ slug, instrument, tf, bars, rate_s: startRate, params: values })
    sessionRef.current = session
    sentRateRef.current = startRate
    const offFrame = session.onFrame((frame) => dispatch({ key, frame }))
    const offFailure = session.onFailure((failure) => {
      if (failure.closeCode !== null && viewFailureEffect(failure.code) === 'fallback') setFell({ key: baseKey, code: failure.code })
      dispatch({ key, failure })
    })
    return () => {
      offFrame()
      offFailure()
      session.close()
      if (sessionRef.current === session) sessionRef.current = null
    }
    // `key` carries the params the view was opened for; `params` itself is read through a ref.
  }, [active, key, baseKey, slug, instrument, tf, bars])

  useEffect(() => {
    if (rate === sentRateRef.current) return
    const session = sessionRef.current
    if (session === null) return
    sentRateRef.current = rate
    session.setRate(rate)
  }, [rate])

  const reconnect = useCallback(() => setAttempt((n) => n + 1), [])
  const view: KeyedMarketState = state.key === key ? state : { ...INITIAL_MARKET_STATE, key }
  const { key: _key, ...rest } = view
  void _key
  return { state: rest, fallbackCode: fell !== null && fellBack ? fell.code : null, reconnect }
}
